import requestV2 from '@/service/request-v2'
import { normalizePageResult } from '@/service/adapters/page-result'
import type { PageQuery, PageResult } from '@/types/crud'

export interface LiveOnlineStatisticsFilters {
  channelId: string | number
  consumerNickName: string
  duration: string | number
  accessTime: string[]
  deptId: string | number
  empIds: string | number
}

export interface LiveOnlineStatisticsRecord {
  id?: string | number
  consumerNickName: string
  consumerHeadImgUrl: string
  channelName: string
  deptName: string
  empName: string
  accessTime: string
  endTime: string
  newDuration: number
}

interface EmployeeRecord {
  id: string | number
  realName: string
}

interface ChannelRecord {
  rowId: string | number
  eventName: string
}

export interface LiveOnlineStatisticsFilterOptions {
  employees: EmployeeRecord[]
  channels: ChannelRecord[]
}

function buildParams(query: PageQuery<LiveOnlineStatisticsFilters>) {
  return {
    ...query.filters,
    pageNo: query.pageNo,
    pageSize: query.pageSize,
    ...(query.sort
      ? { orderBy: query.sort.prop, orderType: query.sort.order }
      : {})
  }
}

function requireArray<T>(payload: unknown, label: string): T[] {
  if (!Array.isArray(payload)) throw new Error(`${label}响应必须是数组`)
  return payload as T[]
}

async function requireExportFile(blob: Blob) {
  if (!blob.type.includes('application/json')) return blob
  const payload = JSON.parse(await blob.text()) as { message?: string }
  throw new Error(payload.message || '导出失败')
}

export async function fetchLiveOnlineStatisticsPage(
  query: PageQuery<LiveOnlineStatisticsFilters>,
  signal: AbortSignal
): Promise<PageResult<LiveOnlineStatisticsRecord>> {
  const payload = await requestV2.get<unknown>({
    url: '/main/liveOnlineStatistics/queryByCondition',
    params: buildParams(query),
    signal
  })
  return normalizePageResult<LiveOnlineStatisticsRecord>(payload)
}

export async function fetchLiveOnlineStatisticsFilterOptions(): Promise<LiveOnlineStatisticsFilterOptions> {
  const [employeePayload, channelPayload] = await Promise.all([
    requestV2.get<unknown>({ url: '/main/employee/queryEmpList' }),
    requestV2.get<unknown>({
      url: '/main/customerEvents/queryByCondition',
      params: { pageNo: 1, pageSize: 1000 }
    })
  ])
  return {
    employees: requireArray<EmployeeRecord>(employeePayload, '员工列表'),
    channels: normalizePageResult<ChannelRecord>(channelPayload).records
  }
}

export function exportLiveOnlineStatisticsDetail(
  query: PageQuery<LiveOnlineStatisticsFilters>
) {
  return requestV2
    .get<Blob>({
      url: '/main/liveOnlineStatistics/download',
      params: buildParams(query),
      responseType: 'blob'
    })
    .then(requireExportFile)
}

export function exportLiveOnlineStatisticsSummary(
  query: PageQuery<LiveOnlineStatisticsFilters>
) {
  return requestV2
    .get<Blob>({
      url: '/main/liveOnlineStatistics/summary',
      params: buildParams(query),
      responseType: 'blob'
    })
    .then(requireExportFile)
}
